import type { JSX, SVGProps } from 'react';

/**
 * The brand LOGOMARK — a crisp little drawn duck for the nav + footer wordmarks (and anywhere the brand
 * needs a sharp glyph at 20–28px). Replaces the flat 🦆 OS emoji, which rendered differently on every
 * platform and never matched our hand-drawn art. This is in-brand: a round yellow body, a friendly amber
 * beak, a soft cheek highlight, and a single dot eye — readable and charming even at nav size.
 *
 * It deliberately echoes the geometry of the real procedural {@link Critter} (round head + side beak +
 * dot eye) so the wordmark and the live world feel like the SAME duck — just at logo scale.
 *
 * ACCESSIBILITY: it always sits beside the visible "Duck Duck Space" text label, so it carries no meaning
 * of its own → `aria-hidden`. Size + layout come from the consumer's `className` (defaults to 1.5rem).
 */
export function DuckMark(props: SVGProps<SVGSVGElement>): JSX.Element {
  return (
    <svg
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
      className="h-6 w-6"
      {...props}
    >
      {/* Soft warm halo so the mark glows a touch on the dark navy bar (premium, alive). */}
      <circle cx="16" cy="16" r="15" fill="#ffd23f" fillOpacity={0.1} />
      {/* Body — the round duck head, our signature yellow with a gentle vertical shade for volume. */}
      <defs>
        <linearGradient id="duckmark-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffe07a" />
          <stop offset="1" stopColor="#f6b829" />
        </linearGradient>
      </defs>
      <circle cx="14.5" cy="15.5" r="9" fill="url(#duckmark-body)" />
      {/* Cheek highlight — the soft top-left light the real critter art carries. */}
      <circle cx="11" cy="12" r="3" fill="#fff3c4" fillOpacity={0.7} />
      {/* Beak — a friendly amber wedge to the side (never crossing the face). */}
      <path d="M22 14.5c3.4-.6 5.4.4 5.4 1.8 0 1.4-2 2.4-5.4 1.8Z" fill="#ff9d2e" />
      <path d="M22 16.3c1.6.2 3 .2 4.4 0" stroke="#e07a16" strokeWidth={0.9} strokeLinecap="round" />
      {/* Eye — a single confident dot with a tiny catchlight. */}
      <circle cx="17.5" cy="13.5" r="1.7" fill="#1c2236" />
      <circle cx="18.1" cy="13" r="0.5" fill="#ffffff" />
    </svg>
  );
}
